import { useState } from "react";
import { Form, Input, notification } from "antd";

import { BasicModal } from "@/ui-components/commonComponents/Modal/BasicModal";
import { SubmitButton } from "@/ui-components/commonComponents/Button/SubmitButton";
import { getFriendlyErrorMessage } from "@/shared/services/http";
import { IInterviewQuestion } from "@/modules/assessments/services/interview-questions-http.service";

import style from "./style.module.css";

interface IAddInterviewQuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  addQuestion: (body: string) => Promise<IInterviewQuestion>;
}

export function AddInterviewQuestionModal({ isOpen, onClose, addQuestion }: IAddInterviewQuestionModalProps) {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSave(values: { body: string }) {
    try {
      setIsSubmitting(true);
      await addQuestion(values.body.trim());
      form.resetFields();
      onClose();
    } catch (error) {
      const message = getFriendlyErrorMessage(error) ?? "Failed to add the interview question. Please try again.";
      notification.error({ message });
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleCancel() {
    form.resetFields();
    onClose();
  }

  return (
    <BasicModal
      isModalOpen={isOpen}
      handleCancel={handleCancel}
      autoHeight
      footer={
        <SubmitButton isLoading={isSubmitting} onClick={() => form.submit()}>
          Save Question
        </SubmitButton>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleSave}>
        <h2 className={style.modalTitle}>Add new Interview question</h2>
        <p className={style.modalDescription}>
          This new interview question will be available to select for all competencies.
        </p>
        <Form.Item
          name="body"
          rules={[{ required: true, whitespace: true, message: "Please write the interview question." }]}
        >
          <Input.TextArea rows={4} placeholder="Write your interview question here" aria-label="Interview question" />
        </Form.Item>
      </Form>
    </BasicModal>
  );
}
